import React, { useEffect, useMemo, useState } from 'react';
import {
  answerChatQuestion,
  listChatQuestionResponses,
  type ChatQuestion,
  type ChatQuestionMyResponse,
  type ChatQuestionResponsesPayload,
} from '../../../../api/chatQuestions';
import { formatDateTimeJST } from '../../../../utils/datetime';
import DsButton from '../../DsButton';
import DsModal from '../../DsModal';

type Props = {
  question?: ChatQuestion | null;
};

const readableError = (error: unknown, fallback: string): string => (
  error instanceof Error && error.message ? error.message : fallback
);

export default function ChatQuestionCard({ question }: Props) {
  const [myResponse, setMyResponse] = useState<ChatQuestionMyResponse | null>(
    question?.my_response || null,
  );
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [answeringId, setAnsweringId] = useState('');
  const [answerError, setAnswerError] = useState('');
  const [responsesOpen, setResponsesOpen] = useState(false);
  const [responses, setResponses] = useState<ChatQuestionResponsesPayload | null>(null);
  const [responsesError, setResponsesError] = useState('');
  const [loadingResponses, setLoadingResponses] = useState(false);
  const isArtist = Object.prototype.hasOwnProperty.call(question || {}, 'my_response');
  const deadlineTime = new Date(question?.response_deadline_at || '').getTime();
  const isExpired = Boolean(question?.is_expired)
    || (Number.isFinite(deadlineTime) && deadlineTime <= currentTime);
  const options = useMemo(
    () => (Array.isArray(question?.options) ? question.options : [])
      .slice()
      .sort((left, right) => left.sort_order - right.sort_order),
    [question?.options],
  );

  useEffect(() => {
    setMyResponse(question?.my_response || null);
  }, [question?.id, question?.my_response]);

  useEffect(() => {
    if (!Number.isFinite(deadlineTime) || deadlineTime <= currentTime) return undefined;
    const timer = window.setTimeout(
      () => setCurrentTime(Date.now()),
      Math.min(deadlineTime - currentTime + 100, 24 * 60 * 60 * 1000),
    );
    return () => window.clearTimeout(timer);
  }, [currentTime, deadlineTime]);

  if (!question) return null;

  const submitAnswer = async (optionId: string) => {
    if (answeringId || myResponse || isExpired) return;
    setAnsweringId(optionId);
    setAnswerError('');
    try {
      const result = await answerChatQuestion(question.id, optionId);
      setMyResponse(result.response);
      window.dispatchEvent(new CustomEvent('chat-question-answered', {
        detail: { questionId: question.id },
      }));
    } catch (error) {
      setAnswerError(readableError(error, '回答を登録できませんでした。'));
    } finally {
      setAnsweringId('');
    }
  };

  const openResponses = async () => {
    setResponsesOpen(true);
    setLoadingResponses(true);
    setResponsesError('');
    try {
      setResponses(await listChatQuestionResponses(question));
    } catch (error) {
      setResponsesError(readableError(error, '回答状況を取得できませんでした。'));
    } finally {
      setLoadingResponses(false);
    }
  };

  return (
    <>
      <section className="chat-question-card" aria-label="質問メッセージ">
        <div className="chat-question-card__eyebrow">
          <i className="fa-solid fa-circle-question" aria-hidden="true" />
          <span>質問メッセージ</span>
          <span className={`chat-question-card__state${isExpired ? ' is-expired' : ''}`}>
            {isExpired ? '期限終了' : myResponse ? '回答済み' : '要回答'}
          </span>
        </div>
        <div className="chat-question-card__question">{question.question_text}</div>
        <div className="chat-question-card__deadline">
          回答期限：{formatDateTimeJST(question.response_deadline_at)}
        </div>
        {isArtist ? (
          <div className="chat-question-card__options">
            {options.map((option) => {
              const selected = String(myResponse?.option_id || '') === String(option.id);
              return (
                <DsButton
                  key={option.id}
                  variant={selected ? 'primary' : 'secondary'}
                  size="sm"
                  block
                  loading={answeringId === option.id}
                  disabled={Boolean(myResponse) || isExpired || Boolean(answeringId)}
                  onClick={() => submitAnswer(option.id)}
                >
                  {selected ? <i className="fa-solid fa-check" aria-hidden="true" /> : null}
                  {option.label}
                </DsButton>
              );
            })}
          </div>
        ) : (
          <DsButton variant="secondary" size="sm" onClick={openResponses}>
            回答状況を見る
            <span className="chat-question-card__count">
              {Number(question.response_summary?.answered_count || 0)}/{Number(question.response_summary?.recipient_count || 0)}
            </span>
          </DsButton>
        )}
        {answerError ? <div className="chat-question-card__error" role="alert">{answerError}</div> : null}
      </section>

      <DsModal
        open={responsesOpen}
        onClose={() => setResponsesOpen(false)}
        title="質問メッセージの回答状況"
        ariaLabel="質問メッセージの回答状況"
        footer={null}
        size="md"
      >
        {loadingResponses ? <div className="chat-question-responses__loading">読み込み中…</div> : null}
        {responsesError ? <div className="chat-question-card__error" role="alert">{responsesError}</div> : null}
        {responses?.summary ? (
          <div className="chat-question-responses__summary">
            回答済み {responses.summary.answered_count}人 / 未回答 {responses.summary.unanswered_count}人
          </div>
        ) : null}
        <div className="chat-question-responses__list">
          {(responses?.responses || []).map((row) => (
            <div className="chat-question-responses__row" key={row.artist_id}>
              <span className="chat-question-responses__name">{row.artist_name}</span>
              <span className={row.responded_at ? 'is-answered' : 'is-unanswered'}>
                {row.responded_at ? row.selected_option_label : '未回答'}
              </span>
            </div>
          ))}
        </div>
      </DsModal>
    </>
  );
}
